import type { WovarProduct } from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import { useErrorAlert } from "@commercekit/react";
import { useCart } from "@commercekit/remix";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { useWvAddToCart } from "../../hooks/useDataLayer";

export const AddToCartLoadingOrSuccess: HTMLComponent<{
  loading: boolean;
  className?: string;
}> = ({ loading, className }) => {
  const isHydrated = useHydrated();
  const [isSuccess, setIsSuccess] = useState(false);
  const hadLoadingRef = useRef(false);

  useEffect(() => {
    if (loading) {
      setIsSuccess(false);
      hadLoadingRef.current = true;
      return;
    }
    if (hadLoadingRef.current) {
      hadLoadingRef.current = false;
      setIsSuccess(true);
      const t = setTimeout(() => setIsSuccess(false), 2000);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Avoid SSR/CSR icon mismatch: only show dynamic icons after hydration. No arbitrary delay.
  if (!isHydrated || (!isSuccess && !loading)) {
    return null;
  }
  return (
    <>
      {loading && !isSuccess ? (
        <i data-icon="" className={`zmdi-hc-spin loading ${className}`}></i>
      ) : (
        <i className={`text-success ${className}`} data-icon="&#xf26b;"></i>
      )}
    </>
  );
};

const WvRelatedProductActionButton: HTMLComponent<{
  product: WovarProduct;
  isSuccess?: boolean;
}> = ({ product, isSuccess = false }) => {
  const { show } = useErrorAlert();

  const { addToCart, loadingQueue, cartLoading, resultQueue } = useCart();

  const isAddingToCart = useMemo(() => {
    return (
      (loadingQueue.find((queue) => queue.id === product.id) && cartLoading) ||
      false
    );
  }, [loadingQueue, cartLoading, product.id]);

  const cartError = useMemo(() => {
    if (!resultQueue?.length) return null;
    const error = resultQueue.find(
      (result) => result.actionFail && result.id === product.id
    );
    return error?.error ? error.error : null;
  }, [resultQueue, product.id]);

  useEffect(() => {
    if (cartError) show(cartError, "warning");
  }, [show, cartError]);
  const gtmAddToCart = useWvAddToCart();
  return (
    <span className="col-auto relatedblock__addtocart-wrapper">
      <div className="relatedblock__addtocart">
        <button
          className="px-1 productblock__addtocart-btn_related"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addToCart({ ...product, quantity: 1 });
            gtmAddToCart(product, 1);
          }}
        >
          {isAddingToCart || isSuccess ? (
            <AddToCartLoadingOrSuccess loading={isAddingToCart} />
          ) : (
            <span className="productblock__addtocart-icon related-popup-icon" data-icon-before="">
              <svg width="25" height="23" xmlns="http://www.w3.org/2000/svg">
                <g fill="#FFF" fillRule="nonzero">
                  <path d="M8.53 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM21.706 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM4.765.059c.45 0 .836.318.923.757l.004.02L8.378 16.06h14.27c.513 0 .93.411.94.922V17a.941.941 0 01-.922.941H7.588a.941.941 0 01-.923-.757l-.004-.02L3.975 1.94H1a.941.941 0 01-.941-.922V1C.059.486.47.069.98.059H4.765z"></path><path d="M23.673 3.864c.636-.205 1.338.338 1.21 1.063l-.005.022-1.694 8.47a.941.941 0 01-.902.757H7.588a.941.941 0 01-.018-1.882h13.92l1.317-6.588H5.706a.941.941 0 01-.941-.923v-.018c0-.514.411-.931.922-.941h17.705c.09 0 .178.012.263.037l.014.004h.004z"></path>
                </g>
              </svg>
            </span>
          )}
        </button>
      </div>
    </span>
  );
};

export default WvRelatedProductActionButton;
